/**
 * websocket.core.ts — WebSocket 连接管理纯函数
 *
 * 从 websocket.worker.ts 中抽取的 WebSocket 生命周期管理逻辑，
 * 不依赖 Worker API、不依赖 DOM，可在 SharedWorker 和主线程降级路径中复用。
 *
 * 职责：
 * - 管理 WebSocket 实例的创建、重连、心跳
 * - 通过 onBroadcast 回调将事件通知外部（Worker 侧→port 广播；降级侧→handler 分发）
 */
import {
  WS_CLOSE_CODE,
  buildRequestFrame,
  calcBackoffMs,
  parseIncomingFrame,
} from './websocket.helpers';

// ── 类型定义 ──────────────────────────────────────────────────────────────────

export interface WsManagerOptions {
  /** 广播回调：当有消息需要通知"端口"时调用（Worker 侧注入 filterAlivePorts，降级侧注入 handler 分发） */
  onBroadcast: (msg: object) => void;
}

export interface WsManager {
  /** 设置 URL 并发起连接（幂等：已连/连接中则 no-op，URL 仅首次设置） */
  connect(wsUrl: string): void;
  /** 发送数据帧（未连接时静默忽略） */
  send(type: string, data: object): void;
  /** 当前是否已连接（WebSocket.OPEN） */
  isConnected(): boolean;
  /** 销毁：关闭 WS、清除所有定时器、重置状态 */
  destroy(): void;
}

// ── 工厂函数 ──────────────────────────────────────────────────────────────────

export function createWsManager(options: WsManagerOptions): WsManager {
  const { onBroadcast } = options;

  // ── 内部状态 ──────────────────────────────────────────────────────────────

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempt = 0;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * 连接稳定计时器：onopen 后 30 秒内未断线，才视为"稳定连接"并重置退避计数。
   * 避免服务端在握手后立即拒绝（如 session 注册失败）时 reconnectAttempt 被清零，
   * 导致退避策略失效、客户端无限 1s 间隔重试。
   */
  let stableTimer: ReturnType<typeof setTimeout> | null = null;
  /** 被踢后置 true，阻止重连（终态，直到 manager.destroy() 被调用） */
  let isKicked = false;
  /** 传入的完整 WS URL（含 token） */
  let wsUrl = '';

  // ── 心跳 ──────────────────────────────────────────────────────────────────

  function startPing(): void {
    stopPing();
    pingTimer = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(buildRequestFrame('ping', {}));
      }
    }, 30000);
  }

  function stopPing(): void {
    if (pingTimer !== null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  }

  // ── 重连调度 ──────────────────────────────────────────────────────────────

  function scheduleReconnect(): void {
    const delay = calcBackoffMs(reconnectAttempt);
    reconnectAttempt++;
    reconnectTimer = setTimeout(connect, delay);
  }

  // ── WebSocket 连接管理 ────────────────────────────────────────────────────

  function connect(newWsUrl: string): void {
    // 幂等：首次设置 URL，后续调用复用同一 URL
    if (!wsUrl) {
      wsUrl = newWsUrl;
    }
    if (isKicked) return;
    if (
      ws?.readyState === WebSocket.CONNECTING ||
      ws?.readyState === WebSocket.OPEN
    )
      return;

    const isReconnect = reconnectAttempt > 0;
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      // 连接建立后延迟 30 秒再重置退避计数，确保连接真正稳定后才清零。
      // 若服务端在 30 秒内关闭连接（如 session 注册失败），stableTimer 被 onclose 取消，
      // reconnectAttempt 保持累积，退避间隔正常加倍。
      stableTimer = setTimeout(() => {
        reconnectAttempt = 0;
        stableTimer = null;
      }, 30000);
      startPing();
      onBroadcast({ event: 'connected', isReconnect });
    };

    ws.onclose = (e) => {
      // DEBUG: 输出关闭码、原因、是否干净关闭，用于验证服务端返回的不同关闭码
      console.log(
        `[WebSocketCore] onclose — code=${e.code}, reason="${e.reason}", wasClean=${e.wasClean}`,
      );
      // 连接未能稳定持续，取消稳定计时器，保留退避计数
      if (stableTimer !== null) {
        clearTimeout(stableTimer);
        stableTimer = null;
      }
      stopPing();
      if (e.code === WS_CLOSE_CODE.KICKED) {
        // 被踢下线：停止重连，通知外部
        isKicked = true;
        onBroadcast({
          event: 'kicked',
          closeCode: e.code,
          closeReason: e.reason,
        });
        return;
      }
      // 普通断线：通知并按指数退避重连
      onBroadcast({
        event: 'disconnected',
        closeCode: e.code,
        closeReason: e.reason,
      });
      scheduleReconnect();
    };

    ws.onmessage = (e: MessageEvent) => {
      const frame = parseIncomingFrame(e.data as string);
      if (!frame) return;
      onBroadcast({ event: 'message', type: frame.type, data: frame.data });
    };
  }

  // ── 公开 API ──────────────────────────────────────────────────────────────

  function send(type: string, data: object): void {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(buildRequestFrame(type, data));
    }
  }

  function isConnected(): boolean {
    return ws?.readyState === WebSocket.OPEN;
  }

  function destroy(): void {
    stopPing();
    if (stableTimer !== null) {
      clearTimeout(stableTimer);
      stableTimer = null;
    }
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (ws) {
      // 先置 null 再 close，避免 onclose 触发重连
      ws.onclose = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.close();
      ws = null;
    }
    isKicked = false;
    reconnectAttempt = 0;
    wsUrl = '';
  }

  return { connect, send, isConnected, destroy };
}
